import { getApiBaseUrl } from "../../shared/api";
import { getAccessToken } from "../../shared/tokenStorage";

const MAX_RECONNECT_DELAY_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_ATTEMPTS = 6;

function wsBaseUrl() {
  const httpBase = getApiBaseUrl();
  if (!httpBase) {
    // Same-origin production build: derive ws(s):// from the current page origin.
    const scheme = window.location.protocol === "https:" ? "wss://" : "ws://";
    return `${scheme}${window.location.host}`;
  }
  return httpBase.replace(/^http/i, "ws");
}

/**
 * Framework-agnostic WebSocket client for the AI assistant. Takes a `dispatch` reference
 * at connect time and forwards parsed frames into `assistantSlice` actions — kept outside
 * React lifecycle (module-level) so it survives widget re-renders/unmounts while open.
 */
export function connectAssistantSocket(sessionId, { dispatch, actions, onOpen, onClose } = {}) {
  let socket = null;
  let attempt = 0;
  let closedByCaller = false;
  let reconnectTimer = null;

  function scheduleReconnect() {
    if (closedByCaller || attempt >= MAX_RECONNECT_ATTEMPTS) {
      dispatch(actions.setConnectionStatus("error"));
      return;
    }
    const delay = Math.min(BASE_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
    const jitter = Math.random() * 300;
    attempt += 1;
    reconnectTimer = setTimeout(open, delay + jitter);
  }

  function open() {
    const token = getAccessToken();
    if (!token) {
      dispatch(actions.setConnectionStatus("error"));
      return;
    }
    dispatch(actions.setConnectionStatus("connecting"));
    const url = `${wsBaseUrl()}/ws/assistant/${sessionId}/?token=${encodeURIComponent(token)}`;
    socket = new WebSocket(url);

    socket.onopen = () => {
      // Real "open" is confirmed by the server's {"type":"connected",...} frame in onmessage,
      // not the raw WS handshake — the backend may still reject on an expired token.
    };

    socket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      switch (data.type) {
        case "connected":
          attempt = 0;
          dispatch(actions.setConnectionStatus("open"));
          dispatch(actions.sessionStarted(data.session_id));
          onOpen && onOpen(data.session_id);
          break;
        case "text_delta":
          dispatch(actions.assistantChunkReceived(data.text || ""));
          break;
        case "chart":
          dispatch(actions.assistantChartReceived(data.chart_spec));
          break;
        case "tool_call_result":
          if (data.message) dispatch(actions.assistantError(data.message));
          if (data.links) dispatch(actions.assistantLinksReceived(data.links));
          else if (data.result) dispatch(actions.assistantToolResultReceived(data.result));
          break;
        case "done":
          dispatch(actions.assistantMessageDone());
          break;
        case "error":
          dispatch(actions.assistantError(data.message || "Assistant error"));
          break;
        default:
          break;
      }
    };

    socket.onclose = () => {
      dispatch(actions.setConnectionStatus("closed"));
      onClose && onClose();
      if (!closedByCaller) scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose fires right after onerror for browser WebSocket — reconnect handled there.
    };
  }

  function send(text) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: "user_message", text }));
    }
  }

  function close() {
    closedByCaller = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (socket) socket.close();
  }

  open();

  return { send, close };
}
